import { useEffect, useState } from 'react';
import { useFetchServiceableUrlsQuery, useLazyFetchServiceableUrlsQuery } from '../service';
import { FetchServiceableUrlsResponse, ServiceableUrl } from '../types';
import { getUrlStringFromObjectEntries } from '../../utils';
import { SERVICEABLE_URLS_ON_PAGE, SERVICEABLE_URLS_POLLING_TIME_MS } from '../../settings';
import useBatchUrlAnalysisProcessesPolling from '../../process/hooks/useBatchUrlAnalysisProcessesPolling';

type Props = {
    websiteGuid: string;
};

export default function useFetchServiceableUrlsPage({ websiteGuid }: Props) {
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<Object | null>(null);

    // Urls returned from the hook
    const [accumulatedUrls, setAccumulatedUrls] = useState<ServiceableUrl[]>([]);

    // For automatic re-loading of pages up until the current one
    const [currentPage, setCurrentPage] = useState<number>(1);

    const { isRunningBatchAnalysis } = useBatchUrlAnalysisProcessesPolling(websiteGuid);

    // First page of urls fetching independently -> specific entry in rtk cache -> on invalidate this refetches
    const {
        data: firstPageServiceableUrls,
        isFetching: isFetchingServiceableUrlsFirstPage,
        isLoading: isLoadingServiceableUrlsFirstPage,
    } = useFetchServiceableUrlsQuery(
        {
            website_guid: websiteGuid,
            limit: SERVICEABLE_URLS_ON_PAGE,
        },
        // If we are running an analysis, refetch if the data is older than 30 seconds
        { refetchOnMountOrArgChange: isRunningBatchAnalysis ? SERVICEABLE_URLS_POLLING_TIME_MS / 1000 : false }
    );

    // n-th page of urls
    const [fetchServiceableUrlsPage, { isFetching: isFetchingServiceableUrlsNextPage }] = useLazyFetchServiceableUrlsQuery();

    const fetchPage = async (lastEvaluatedKey: Object) => {
        const fetchUrlsPromise = fetchServiceableUrlsPage(
            {
                website_guid: websiteGuid,
                limit: SERVICEABLE_URLS_ON_PAGE,
                last_evaluated_key: getUrlStringFromObjectEntries(lastEvaluatedKey),
            },
            !isRunningBatchAnalysis
        );

        fetchUrlsPromise.unsubscribe();
        const nextPage = await fetchUrlsPromise.unwrap();
        return nextPage;
    };

    // On load more fetch next page separately and add to accumulated urls
    const handleLoadMore = async () => {
        if (lastEvaluatedKey) {
            try {
                const nextPage = await fetchPage(lastEvaluatedKey);

                setAccumulatedUrls([...accumulatedUrls, ...nextPage.data]);
                setLastEvaluatedKey(nextPage.meta.last_evaluated_key);
                setCurrentPage(currentPage + 1);
            } catch (error) {}
        }
    };

    // Whenever the first page refetches replace everything with the new first page
    useEffect(() => {
        let shouldSetState = true;

        const reloadPages = async (firstPage: FetchServiceableUrlsResponse) => {
            let currentLastEvaluatedKey = firstPage.meta.last_evaluated_key;
            let currentUrls = firstPage.data;

            for (let page = currentPage; page > 1 && currentLastEvaluatedKey; page--) {
                const nextPage = await fetchPage(currentLastEvaluatedKey);

                if (nextPage) {
                    currentUrls = [...currentUrls, ...nextPage.data];
                    currentLastEvaluatedKey = nextPage.meta.last_evaluated_key;
                }
            }

            if (shouldSetState) {
                setAccumulatedUrls(currentUrls);
                setLastEvaluatedKey(currentLastEvaluatedKey);
            }
        };

        if (!isFetchingServiceableUrlsFirstPage && firstPageServiceableUrls?.data) {
            reloadPages(firstPageServiceableUrls);
        }

        return () => {
            shouldSetState = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstPageServiceableUrls, isFetchingServiceableUrlsFirstPage]);

    return {
        serviceableUrls: accumulatedUrls,
        isFetchingServiceableUrlsNextPage,
        isLoadingServiceableUrlsFirstPage,
        hasMore: lastEvaluatedKey !== null,
        handleLoadMore,
    };
}
